// RouterContext is inlined here to avoid importing from backend.
// This keeps the transport layer fully decoupled from the backend.
import { clearSessionAndRedirectToLogin } from './onAuthFailure';
import { dispatchGlobalToast } from '../../utils/globalToast';
import { getClerkToken } from './clerkAuth';
import { useAppStore } from '../../store';
import { trimTrailingSlashes } from '../../utils/strings';
import { DEMO_PRODUCTION_HOSTNAME } from '../../utils/demoClock';
import { getOrCreateCorrelationId } from '../../utils/correlationId';
import { CORRELATION_ID_HEADER } from '@repo/shared-types';
import { buildApiContractTelemetryProperties, getPostHogInstance, logger, parseCentsScalar, SchemaViolationError } from '@repo/shared-utils';
import { sanitizeForClientTelemetry } from '../clientTelemetrySanitize';

/**
 * When `demo.hummingbirdpmp.com` is served without `VITE_API_ORIGIN`, the SPA still
 * needs a public API base. Must match Railway **staging/testing** API + CORS allowlist
 * (see docs/RAILWAY_ENVIRONMENTS.md). Override anytime with `VITE_API_ORIGIN` on Vercel.
 */
const DEMO_HOST_PUBLIC_API_ORIGIN =
  'https://hummingbird-property-management-platform-stagingtesting.up.railway.app';

export interface RouterContext {
  currentUserId: string;
  userRole: string;
  isSelfManagingOwner?: boolean;
}

type RpcMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type RpcPayload = {
  method: RpcMethod;
  path: string;
  body?: unknown;
  context?: RouterContext;
  /** Forwarded by standalone-server for guest routes (e.g. onboarding upload secret). */
  clientHeaders?: Record<string, string>;
};

export type QueryFilters = Record<string, string | number | boolean | string[]>;

export function getApiOrigin(): string {
  let origin: string | undefined;
  try {
    if (typeof import.meta !== 'undefined') {
      const env = (import.meta as { env?: Record<string, string> }).env;
      origin = env?.VITE_API_ORIGIN?.trim();
    }
  } catch (err) {
    logger.debug('[transport] getApiOrigin import.meta.env access failed', err);
  }
  if (origin == null || origin === '') {
    const nodeEnv = typeof process === 'undefined' ? undefined : process?.env;
    origin = nodeEnv?.VITE_API_ORIGIN?.trim() ?? nodeEnv?.API_ORIGIN?.trim();
  }
  if (!origin) {
    const inBrowser = typeof window !== 'undefined';
    if (!inBrowser) {
      return 'http://localhost';
    }
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return window.location.origin;
    }
    if (host === DEMO_PRODUCTION_HOSTNAME) {
      return trimTrailingSlashes(DEMO_HOST_PUBLIC_API_ORIGIN);
    }
    throw new Error(
      'VITE_API_ORIGIN is required in production and preview. Set VITE_API_ORIGIN in your deployment environment (e.g. Vercel env vars).'
    );
  }
  return trimTrailingSlashes(origin);
}

const _isLocalBrowserHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

function withFilters(path: string, filters?: QueryFilters): string {
  if (!filters || Object.keys(filters).length === 0) return path;
  const url = new URL(path, 'http://localhost');
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      for (const v of value) url.searchParams.append(key, String(v));
    } else {
      url.searchParams.set(key, String(value));
    }
  }
  return `${url.pathname}${url.search}`;
}

/** Stable `error.code` values from `serializeRpcError` (non-integration). */
export const RPC_ERROR_CODE = {
  TRUST_ACCOUNT_MISSING: 'TRUST_ACCOUNT_MISSING',
  TRUST_ACCOUNT_TYPE: 'TRUST_ACCOUNT_TYPE',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  DOMAIN_VALIDATION_FAILED: 'DOMAIN_VALIDATION_FAILED',
  FORBIDDEN: 'FORBIDDEN',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
} as const;

/** Stable codes from `serializeOnboardingUploadError` / pre-signup upload routes. */
export const ONBOARDING_UPLOAD_ERROR_CODE = {
  VALIDATION: 'ONBOARDING_UPLOAD_VALIDATION',
  NOT_FOUND: 'ONBOARDING_UPLOAD_NOT_FOUND',
  CONSUMED: 'ONBOARDING_UPLOAD_CONSUMED',
  SESSION_INVALID: 'ONBOARDING_UPLOAD_SESSION_INVALID',
} as const;

export type OnboardingUploadErrorCode =
  (typeof ONBOARDING_UPLOAD_ERROR_CODE)[keyof typeof ONBOARDING_UPLOAD_ERROR_CODE];

export type KnownRpcApiErrorCode =
  | (typeof RPC_ERROR_CODE)[keyof typeof RPC_ERROR_CODE]
  | OnboardingUploadErrorCode;

/** True if `err` is an `RpcApiError` from an onboarding upload route with optional `code` filter. */
export function isOnboardingUploadRpcApiError(
  err: unknown,
  code?: OnboardingUploadErrorCode
): err is RpcApiError {
  if (!(err instanceof RpcApiError) || err.code == null) return false;
  const c = err.code;
  if (
    c !== ONBOARDING_UPLOAD_ERROR_CODE.VALIDATION &&
    c !== ONBOARDING_UPLOAD_ERROR_CODE.NOT_FOUND &&
    c !== ONBOARDING_UPLOAD_ERROR_CODE.CONSUMED &&
    c !== ONBOARDING_UPLOAD_ERROR_CODE.SESSION_INVALID
  ) {
    return false;
  }
  return code == null || err.code === code;
}

/** Known codes plus `INTEGRATION_*` from the API integration normalizer. */
export type RpcApiErrorCode = KnownRpcApiErrorCode | `INTEGRATION_${string}`;

export type ParsedRpcErrorFields = {
  code?: RpcApiErrorCode;
  details?: Record<string, unknown>;
  provider?: string;
  retryable?: boolean;
  providerCode?: string;
  field?: string;
  requestId?: string;
  /** HTTP status from the RPC response when thrown by transport (not from JSON body). */
  statusCode?: number;
};

export type ParsedRpcError = { message: string } & ParsedRpcErrorFields;

function readPlainRecord(v: unknown): Record<string, unknown> | undefined {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return undefined;
  return v as Record<string, unknown>;
}

function parseNestedErrorFields(rawErr: Record<string, unknown>): ParsedRpcErrorFields {
  const codeRaw = rawErr.code;
  const code =
    typeof codeRaw === 'string' && codeRaw.length > 0 ? (codeRaw as RpcApiErrorCode) : undefined;
  const provider = typeof rawErr.provider === 'string' ? rawErr.provider : undefined;
  const retryable = typeof rawErr.retryable === 'boolean' ? rawErr.retryable : undefined;
  const providerCode = typeof rawErr.providerCode === 'string' ? rawErr.providerCode : undefined;
  const field = typeof rawErr.field === 'string' ? rawErr.field : undefined;
  const requestId = typeof rawErr.requestId === 'string' ? rawErr.requestId : undefined;
  const details = readPlainRecord(rawErr.details);
  const out: ParsedRpcErrorFields = { code, provider, retryable, providerCode, field, requestId };
  if (details) out.details = details;
  return out;
}

/**
 * Parse RPC error JSON: `{ error: { code?, message, details?, … } }` or string `error`.
 * UI should branch on `code`, not on HTTP status (status is only used here for fallback text).
 */
export function parseRpcErrorPayload(parsed: unknown, httpFallbackStatus: number): ParsedRpcError {
  const fallbackMessage = `API request failed (${httpFallbackStatus})`;
  if (typeof parsed !== 'object' || parsed === null) {
    return { message: fallbackMessage };
  }
  const p = parsed as Record<string, unknown>;
  const rawErr = p.error;
  if (typeof rawErr === 'string' && rawErr.length > 0) {
    return { message: rawErr };
  }
  if (rawErr && typeof rawErr === 'object') {
    const o = rawErr as Record<string, unknown>;
    const msg = o.message;
    if (typeof msg === 'string' && msg.length > 0) {
      return { message: msg, ...parseNestedErrorFields(o) };
    }
  }
  return { message: fallbackMessage };
}

/** @deprecated Prefer `RPC_ERROR_CODE.TRUST_ACCOUNT_MISSING` */
export const RPC_ERROR_CODE_TRUST_ACCOUNT_MISSING = RPC_ERROR_CODE.TRUST_ACCOUNT_MISSING;

/**
 * Thrown when `POST /api` returns a non-2xx body.
 * Prefer `statusCode` (set by transport) for HTTP-level branching; use `code` / `message` for API errors.
 */
export class RpcApiError extends Error {
  readonly code?: RpcApiErrorCode;
  readonly details?: Record<string, unknown>;
  readonly provider?: string;
  readonly retryable?: boolean;
  readonly providerCode?: string;
  readonly field?: string;
  readonly requestId?: string;
  readonly statusCode?: number;

  constructor(message: string, fields?: ParsedRpcErrorFields) {
    super(message);
    this.name = 'RpcApiError';
    if (fields) {
      this.code = fields.code;
      if (fields.details !== undefined) this.details = fields.details;
      if (fields.provider !== undefined) this.provider = fields.provider;
      if (fields.retryable !== undefined) this.retryable = fields.retryable;
      if (fields.providerCode !== undefined) this.providerCode = fields.providerCode;
      if (fields.field !== undefined) this.field = fields.field;
      if (fields.requestId !== undefined) this.requestId = fields.requestId;
      if (fields.statusCode !== undefined) this.statusCode = fields.statusCode;
    }
    Object.setPrototypeOf(this, RpcApiError.prototype);
  }
}

/** True when the RPC layer received HTTP 403 (e.g. RBAC); use for trust badges — do not retry. */
export function isRpcApiForbidden(err: unknown): boolean {
  return err instanceof RpcApiError && err.statusCode === 403;
}

/**
 * Trust & Safety read routes: 403 must not redirect to /app/unauthorized so UI can show a neutral hidden badge.
 */
export function isTrustSafetyReadPath(path: string, method: RpcMethod): boolean {
  if (method !== 'GET') return false;
  if (/^\/tenants\/[^/]+\/stripe-verification\/?$/.test(path)) return true;
  if (/^\/maintenance\/vendors\/[^/]+\/screenings\/?$/.test(path)) return true;
  if (/^\/maintenance\/vendors\/[^/]+\/tax-summaries\/?$/.test(path)) return true;
  if (/^\/leasing\/deposit-settlements\/[^/]+\/pdf\/?$/.test(path)) return true;
  if (/^\/leasing\/deposit-settlements\/[^/]+\/preview-pdf\/?$/.test(path)) return true;
  return false;
}

function normalizeCentsStringFields(value: unknown, path: string = '$'): unknown {
  if (Array.isArray(value)) {
    return value.map((item, idx) => normalizeCentsStringFields(item, `${path}[${idx}]`));
  }
  if (value == null || typeof value !== 'object') return value;

  const proto = Object.getPrototypeOf(value);
  const isPlainObject = proto === Object.prototype || proto === null;
  if (!isPlainObject) return value;

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (k.endsWith('CentsString')) {
      const targetKey = k.slice(0, -'String'.length);
      if (v == null) {
        out[targetKey] = v;
      } else {
        out[targetKey] = parseCentsScalar(v as string | number | bigint, `${path}.${k}`);
      }
      continue;
    }
    out[k] = normalizeCentsStringFields(v, `${path}.${k}`);
  }
  return out;
}

export function buildRpcFetchHeaders(
  bearerToken: string | null,
): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }
  const correlationId = getOrCreateCorrelationId();
  if (correlationId) {
    headers[CORRELATION_ID_HEADER] = correlationId;
  }
  return headers;
}

const RATE_LIMIT_TOAST =
  "We're experiencing heavy traffic. Please try again in a moment.";
const RATE_LIMIT_BACKOFF_MS = [800, 1600] as const;
const RATE_LIMIT_MAX_RETRIES = 2;

async function remoteRpc<T>(payload: RpcPayload, retryCount = 0): Promise<T> {
  const origin = getApiOrigin();
  const clerkToken = await getClerkToken();
  const bearerToken = clerkToken ?? null;
  let res: Response;
  try {
    res = await fetch(`${origin}/api`, {
      method: 'POST',
      headers: buildRpcFetchHeaders(bearerToken),
      body: JSON.stringify(payload),
    });
  } catch (networkErr) {
    if (retryCount < 1) {
      await new Promise((r) => setTimeout(r, 500));
      return remoteRpc<T>(payload, retryCount + 1);
    }
    throw networkErr;
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      logger.debug('[transport] remoteRpc response JSON parse fallback', err);
      parsed = text;
    }
  }

  if (!res.ok) {
    if (res.status >= 500 && retryCount < 1) {
      await new Promise((r) => setTimeout(r, 500));
      return remoteRpc<T>(payload, retryCount + 1);
    }
    const parsedErr = parseRpcErrorPayload(parsed, res.status);
    const { message, ...errFields } = parsedErr;
    const apiErr = () => new RpcApiError(message, { ...errFields, statusCode: res.status });

    if (res.status === 401) {
      if (clerkToken) {
        // Don't redirect to login for complete-clerk-onboarding: let the view show the error.
        if (payload.path === '/auth/complete-clerk-onboarding') {
          throw apiErr();
        }
        // `/auth/current-user` 401 with a Bearer: invalid/expired JWT, wrong Clerk instance vs
        // `CLERK_SECRET_KEY`, or no `UserProfile.clerkUserId` for this `sub`. Do **not** redirect to
        // `/complete-onboarding` here — that interacted badly with `ClerkAuthBridge` + landing
        // (`/?join=1`) and caused a navigation loop. Onboarding uses `getCurrentUserIfExists` +
        // sessionStorage `pendingClerkOnboarding` instead.
        if (payload.path === '/auth/current-user') {
          throw apiErr();
        }
        if (payload.path === '/onboarding/clerk-handoff') {
          throw apiErr();
        }
        useAppStore.getState().setClerkSignedIn(false);
        clearSessionAndRedirectToLogin();
        throw apiErr();
      }
      clearSessionAndRedirectToLogin();
      throw apiErr();
    }
    if (res.status === 429) {
      if (retryCount === 0) {
        dispatchGlobalToast(RATE_LIMIT_TOAST, 'info');
      }
      if (retryCount < RATE_LIMIT_MAX_RETRIES) {
        const delay = RATE_LIMIT_BACKOFF_MS[retryCount] ?? 2000;
        await new Promise((r) => setTimeout(r, delay));
        return remoteRpc<T>(payload, retryCount + 1);
      }
      throw apiErr();
    }

    if (res.status === 403) {
      // Resource-level / RLS denials: surface to the caller; route guards handle role mismatch.
      throw apiErr();
    }

    throw apiErr();
  }

  // Response contract validation (dev + strict registry/audited routes in prod)
  try {
    const { validateResponseContract, isStrictResponseContractPath } = await import(
      './responseSchemas'
    );
    const contractPath = payload.path.split('?')[0];
    const strictRoute = isStrictResponseContractPath(payload.method, contractPath);
    if (import.meta.env.DEV || strictRoute) {
      const outcome = validateResponseContract(payload.method, contractPath, parsed);
      const issueText =
        outcome.kind === 'invalid'
          ? outcome.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')
          : undefined;

      if (outcome.kind === 'uncovered' && strictRoute) {
        logger.error(
          `[API Contract] ${payload.method} ${payload.path} — missing response schema (strict contract route)`,
          {
            module: 'apiTransport',
            action: 'validate_response_contract',
            method: payload.method,
            path: payload.path,
            currentUserId: payload.context?.currentUserId ?? undefined,
          },
        );
      } else if (outcome.kind === 'invalid') {
        const logFn = strictRoute ? logger.error.bind(logger) : logger.warn.bind(logger);
        logFn(
          `[API Contract] ${payload.method} ${payload.path} — ${outcome.issues.length} issue(s)`,
          {
            module: 'apiTransport',
            action: 'validate_response_contract',
            method: payload.method,
            path: payload.path,
            currentUserId: payload.context?.currentUserId ?? undefined,
            issues: issueText,
          },
        );
        if (strictRoute) {
          const posthog = getPostHogInstance();
          if (posthog && typeof posthog.capture === 'function') {
            const telemetry = buildApiContractTelemetryProperties(payload.method, contractPath, {
              issue_count: outcome.issues.length,
              contract_status: 'invalid',
            });
            const safeProps = sanitizeForClientTelemetry({
              ...telemetry,
              issues_preview: issueText?.slice(0, 400) ?? '',
            }) as Record<string, unknown>;
            posthog.capture('api_contract_invalid', safeProps);
          }
        }
      }
    }
  } catch (err) {
    logger.debug('[transport] response schema validation skipped', err);
  }

  try {
    return normalizeCentsStringFields(parsed) as T;
  } catch (err) {
    if (err instanceof SchemaViolationError) {
      logger.error('[transport] cents transport schema violation', {
        module: 'apiTransport',
        action: 'normalize_cents_transport',
        method: payload.method,
        path: payload.path,
        correlationId: getOrCreateCorrelationId(),
        fieldPath: err.fieldPath,
        error: err.message,
        payloadPreview:
          parsed && typeof parsed === 'object'
            ? JSON.stringify(parsed).slice(0, 1500)
            : String(parsed),
      });
      throw new RpcApiError('Invalid money data received from API.', {
        code: RPC_ERROR_CODE.VALIDATION_FAILED,
        details: {
          fieldPath: err.fieldPath,
          cause: err.message,
        },
      });
    }
    throw err;
  }
}

/** Plain helper: whether to use remote API (env/window only). Not a React hook. */
function _shouldUseRemoteApi(): boolean {
  // Always use remote API now that we are enforcing network boundary.
  // The 'router' import has been removed to reduce coupling.
  return true;
}

export const apiTransport = {
  get: async <T>(
    path: string,
    context?: RouterContext,
    filters?: QueryFilters,
    clientHeaders?: Record<string, string>
  ): Promise<T> => {
    const resolvedPath = withFilters(path, filters);
    return remoteRpc<T>({ method: 'GET', path: resolvedPath, context, clientHeaders });
  },

  post: async <T>(path: string, body?: unknown, context?: RouterContext): Promise<T> => {
    return remoteRpc<T>({ method: 'POST', path, body, context });
  },

  patch: async <T>(path: string, body?: unknown, context?: RouterContext): Promise<T> => {
    return remoteRpc<T>({ method: 'PATCH', path, body, context });
  },

  put: async <T>(
    path: string,
    body?: unknown,
    context?: RouterContext,
    clientHeaders?: Record<string, string>
  ): Promise<T> => {
    return remoteRpc<T>({ method: 'PUT', path, body, context, clientHeaders });
  },

  delete: async <T>(path: string, context?: RouterContext): Promise<T> => {
    return remoteRpc<T>({ method: 'DELETE', path, context });
  },
};
